import { motion } from 'framer-motion';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import type React from 'react';
import { Virtuoso } from 'react-virtuoso';

import { getPhotoCardConfirmedStatus } from '@pocapan/shared/utils/PhotoCardUtil';
import { EventNameBox } from '../../event/EventNameBox';
import CheckIcon from '../../icons/CheckIcon';
import ToggleAllCardsButton from '../../../presentation/template/edit-collected-photo-cards/components/ToggleAllCardsButton';

import type { EventType, PhotoCardType } from '../../../presentation/types/PhotoCardType';

interface NewPhotoCardsByEventEditorProps {
  events: EventType[];
  onClickedPhotoCard: (photoCard: PhotoCardType, event?: EventType) => void;
  endReached: () => void;
  isColorMode: boolean;
  isAdminMode?: boolean;
  selectedCollectedData?: Record<string, Record<string, { collectedCount: number }>>;
  currentMember?: { memberId: string } | null;
}

const NewPhotoCardsByEventEditor: React.FC<NewPhotoCardsByEventEditorProps> = ({
  events,
  onClickedPhotoCard,
  endReached,
  isColorMode,
  isAdminMode = false,
  selectedCollectedData = {},
  currentMember = null
}) => {
  const isSelectedCard = (photoCard: PhotoCardType): boolean => {
    if (isAdminMode) {
      return photoCard.isChecked ?? false;
    }

    // 실제 사용자가 선택한 카드인지 확인
    const memberId = photoCard.memberId || currentMember?.memberId;
    if (!memberId) return false;

    return selectedCollectedData[memberId]?.[photoCard.photoCardId] !== undefined;
  };

  const getStatusBadgeClasses = (pendingStatus: string): string => {
    const baseClasses =
      'absolute top-0 left-0 z-50 text-white w-fit inline-block rounded px-2 py-1 font-semibold text-[13px] leading-4';

    switch (pendingStatus) {
      case 'approved':
        return `${baseClasses} bg-primary-light dark:bg-primary-dark`;
      case 'pending':
        return `${baseClasses} bg-main-light-2 dark:bg-main-dark-2`;
      default:
        return `${baseClasses} bg-warning`;
    }
  };

  const getOriginalCount = (photoCard: PhotoCardType): number => {
    return photoCard.originalCollectedCount !== undefined
      ? photoCard.originalCollectedCount
      : photoCard.collectedCount;
  };

  const getImageClasses = (photoCard: PhotoCardType): string => {
    const baseClasses = 'w-full h-full object-cover rounded-lg cursor-pointer';
    const originalCount = getOriginalCount(photoCard);
    const filterClass = originalCount > 0 || isColorMode ? 'grayscale-0' : 'grayscale';

    return `${baseClasses} ${filterClass}`;
  };

  const getOverlayStyle = (photoCard: PhotoCardType) => {
    const originalCount = getOriginalCount(photoCard);
    const isSelected = isSelectedCard(photoCard);
    const hasOriginal = originalCount > 0;

    const baseClasses = 'absolute inset-0 rounded-[8px] pointer-events-none';

    const borderClasses =
      isSelected || (isColorMode && hasOriginal) ? 'ring-[2px] ring-inset ring-[#5C3DFA]' : '';

    const gradientClasses = isSelected
      ? ''
      : isColorMode || hasOriginal
        ? 'bg-[linear-gradient(0deg,_rgba(0,_0,_0,_0.75)_0%,_rgba(0,_0,_0,_0.08)_50.31%,_rgba(0,_0,_0,_0.05)_100%)]'
        : 'bg-[linear-gradient(0deg,_rgba(0,_0,_0,_0.75)_0%,_rgba(0,_0,_0,_0)_52.5%)]';

    return `${baseClasses} ${borderClasses} ${gradientClasses}`.trim();
  };

  const renderPhotoCard = (photoCard: PhotoCardType, event: EventType) => {
    const pendingStatus = getPhotoCardConfirmedStatus(photoCard);

    return (
      <motion.div
        key={photoCard.photoCardId}
        initial={{ opacity: 0, y: -1 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -1 }}
        transition={{ duration: 0.5 }}
        className="aspect-[75/118]"
      >
        <div className="relative aspect-[75/118]">
          {isAdminMode && (
            <div className={getStatusBadgeClasses(pendingStatus)}>
              {t(`ReportedPhotoCardsPage.${pendingStatus}`)}
            </div>
          )}

          <img
            src={photoCard.mediumThumbnailUrl || photoCard.pictureUrl}
            className={getImageClasses(photoCard)}
            onClick={() => onClickedPhotoCard(photoCard, event.event)}
            loading="lazy"
            alt="Card"
          />

          {/* Gradient 오버레이 */}
          <div className={getOverlayStyle(photoCard)} />

          {(photoCard.categoryEmoji || photoCard.categoryName) && !isSelectedCard(photoCard) && (
            <div className="absolute inset-x-0 bottom-0 m-[7px]">
              <div className="text-pure-white text-[11px] font-semibold leading-none">
                {photoCard.categoryEmoji || ''} {photoCard.categoryName || ''}
              </div>
            </div>
          )}

          {isSelectedCard(photoCard) && (
            <div className="absolute bottom-0 right-0 bg-onSecondary-light rounded-tl-[8px] rounded-br-[8px] p-[3px]">
              <CheckIcon className="fill-white w-[16px] h-[16px]" />
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const drawCardsWithEventName = (event: EventType) => {
    if (isEmpty(event.photoCards)) {
      return <div className="h-px" />;
    }

    return (
      <div key={event.eventId} className="pb-[8px]">
        <div className="overflow-hidden mt-[12px] mb-[9px] flex items-center justify-between">
          <EventNameBox event={event} />
          <ToggleAllCardsButton
            photoCards={event.photoCards.map((card) => ({
              photoCardId: card.photoCardId,
              memberId: card.memberId,
              collectedCount: card.collectedCount
            }))}
            selectedCollectedData={selectedCollectedData}
            currentMember={currentMember}
            onToggleAll={(_cards, isSelect) => {
              event.photoCards.forEach((card) => {
                const isCurrentlySelected =
                  selectedCollectedData[currentMember?.memberId || '']?.[card.photoCardId];
                if (isSelect !== !!isCurrentlySelected) {
                  onClickedPhotoCard(card, event.event);
                }
              });
            }}
          />
        </div>

        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-6 gap-[8px]">
          {event.photoCards?.map((photoCard) => renderPhotoCard(photoCard, event))}
        </div>
      </div>
    );
  };

  return (
    <Virtuoso
      useWindowScroll
      style={{ height: '100%' }}
      endReached={endReached}
      overscan={480}
      data={events}
      itemContent={(_, event) => (
        <motion.div
          key={event.eventId}
          initial={{ opacity: 0, y: -1 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -1 }}
          transition={{ duration: 0.5 }}
        >
          {drawCardsWithEventName(event)}
        </motion.div>
      )}
    />
  );
};

export default NewPhotoCardsByEventEditor;
